import { sortMatchesChronologically } from './dateFormat';

/**
 * Partition matches by stage (group vs finals) and by (matchType, groupId).
 * Group stage: phase === 'group' or round === 1; has groupId (A, B, ...).
 * Finals: phase === 'finals' or round === 2; groupId null.
 * Returns { groupStage: { singles: { A: [], B: [] }, doubles: { A: [], B: [] } }, finals: { singles: [], doubles: [] } }.
 */
export function partitionByGroupAndStage(matches) {
  const groupStage = { singles: {}, doubles: {} };
  const finals = { singles: [], doubles: [] };

  for (const m of matches) {
    const isFinals = m.phase === 'finals' || m.round === 2;
    const type = m.matchType === 'doubles' ? 'doubles' : 'singles';

    if (isFinals) {
      finals[type].push(m);
    } else {
      const g = m.groupId || 'A';
      if (!groupStage[type][g]) groupStage[type][g] = [];
      groupStage[type][g].push(m);
    }
  }

  const groupIds = (t) => Object.keys(groupStage[t]).sort();
  for (const t of ['singles', 'doubles']) {
    for (const g of groupIds(t)) {
      groupStage[t][g].sort(sortMatchesChronologically);
    }
    finals[t].sort(sortMatchesChronologically);
  }

  return { groupStage, finals };
}

/**
 * Compute standings (wins, losses) from completed matches in a group.
 * Singles: key = playerId, name = display name.
 * Doubles: key = teamId || teamName || team1Players.join(','), name = teamName || 'P1 & P2'.
 * Returns sorted array of { rank, key, name, wins, losses } (wins desc, losses asc).
 */
export function computeStandings(matches, matchType, getPlayerName) {
  const completed = matches.filter((m) => m.status === 'completed');
  const byKey = new Map();

  const ensure = (key, name) => {
    if (!byKey.has(key)) byKey.set(key, { key, name, wins: 0, losses: 0 });
    return byKey.get(key);
  };

  for (const m of completed) {
    if (matchType === 'singles') {
      const p1 = m.player1Id;
      const p2 = m.player2Id;
      const n1 = m.player1Name || (getPlayerName ? getPlayerName(p1) : null) || 'Player 1';
      const n2 = m.player2Name || (getPlayerName ? getPlayerName(p2) : null) || 'Player 2';
      ensure(p1, n1);
      ensure(p2, n2);
      const w = m.winnerId || (m.winnerIds && m.winnerIds[0]);
      if (w === p1) {
        byKey.get(p1).wins++;
        byKey.get(p2).losses++;
      } else if (w === p2) {
        byKey.get(p2).wins++;
        byKey.get(p1).losses++;
      }
    } else {
      const k1 = m.team1Id || m.team1Name || (m.team1Players || []).slice().sort().join(',');
      const k2 = m.team2Id || m.team2Name || (m.team2Players || []).slice().sort().join(',');
      const n1 = (getPlayerName && m.team1Players?.length)
        ? (m.team1Players.map((id) => getPlayerName(id)).filter(Boolean).join(' & ') || m.team1Name || 'Team 1')
        : (m.team1Name || (m.team1PlayerNames && m.team1PlayerNames.length ? m.team1PlayerNames.join(' & ') : null) || 'Team 1');
      const n2 = (getPlayerName && m.team2Players?.length)
        ? (m.team2Players.map((id) => getPlayerName(id)).filter(Boolean).join(' & ') || m.team2Name || 'Team 2')
        : (m.team2Name || (m.team2PlayerNames && m.team2PlayerNames.length ? m.team2PlayerNames.join(' & ') : null) || 'Team 2');
      ensure(k1, n1);
      ensure(k2, n2);
      const winnerId = m.winnerId || (m.winnerIds && m.winnerIds[0]);
      if (winnerId && m.team1Players && m.team1Players.includes(winnerId)) {
        byKey.get(k1).wins++;
        byKey.get(k2).losses++;
      } else if (winnerId && m.team2Players && m.team2Players.includes(winnerId)) {
        byKey.get(k2).wins++;
        byKey.get(k1).losses++;
      }
    }
  }

  // Include participants from any match (including scheduled) with 0–0 if not yet in byKey
  for (const m of matches) {
    if (matchType === 'singles') {
      const p1 = m.player1Id;
      const p2 = m.player2Id;
      const n1 = m.player1Name || (getPlayerName ? getPlayerName(p1) : null) || 'Player 1';
      const n2 = m.player2Name || (getPlayerName ? getPlayerName(p2) : null) || 'Player 2';
      ensure(p1, n1);
      ensure(p2, n2);
    } else {
      const k1 = m.team1Id || m.team1Name || (m.team1Players || []).slice().sort().join(',');
      const k2 = m.team2Id || m.team2Name || (m.team2Players || []).slice().sort().join(',');
      const n1 = (getPlayerName && m.team1Players?.length)
        ? (m.team1Players.map((id) => getPlayerName(id)).filter(Boolean).join(' & ') || m.team1Name || 'Team 1')
        : (m.team1Name || (m.team1PlayerNames && m.team1PlayerNames.length ? m.team1PlayerNames.join(' & ') : null) || 'Team 1');
      const n2 = (getPlayerName && m.team2Players?.length)
        ? (m.team2Players.map((id) => getPlayerName(id)).filter(Boolean).join(' & ') || m.team2Name || 'Team 2')
        : (m.team2Name || (m.team2PlayerNames && m.team2PlayerNames.length ? m.team2PlayerNames.join(' & ') : null) || 'Team 2');
      ensure(k1, n1);
      ensure(k2, n2);
    }
  }

  const list = Array.from(byKey.values()).sort((a, b) => b.wins - a.wins || a.losses - b.losses);
  return list.map((r, i) => ({ ...r, rank: i + 1 }));
}
